import React from 'react';

import { SEMANTIC_TYPES } from '@/detectors/semanticTypes';
import type { ISemanticType } from '@/interfaces/ISemanticType';
import type { IVisualizerTool, VisualizerRenderContext } from '@/interfaces/ITool';
import { parseRegex } from '@/lib/regex/parseRegex';
import { regexAstToGraph } from '@/lib/regex/regexAstToGraph';

import type { RegexSegment } from './RegexDiagramView';

const LazyRegexDiagramView = React.lazy(() =>
  import('./RegexDiagramView').then((m) => ({ default: m.RegexDiagramView }))
);

class RegexRailroadVisualizerToolImpl implements IVisualizerTool {
  readonly id = 'regex-railroad-visualizer';
  readonly name = 'Regex Railroad Visualizer';
  readonly description = 'Visualize a JavaScript regular expression as a simple railroad diagram';
  readonly version = '0.1.0';
  readonly type = 'visualizer' as const;
  readonly category = 'Regex Tools';
  readonly editable = true;
  readonly keywords = ['regex', 'regexp', 'railroad', 'diagram', 'visualize', 'regulex', 'visualizer', 'pattern', 'state machine', 'nfa'];

  readonly supportedInputTypes: ISemanticType[] = [SEMANTIC_TYPES.REGEX, SEMANTIC_TYPES.TEXT];
  readonly outputType = SEMANTIC_TYPES.TEXT;

  supportsInput(): boolean {
    return true;
  }

  readonly attributes = [
    {
      name: 'inputFormat',
      type: 'select' as const,
      label: 'Input Format',
      defaultValue: 'auto',
      options: ['auto', 'pattern', 'literal'],
      description: 'Auto accepts /pattern/flags, otherwise uses Pattern + Flags',
    },
    {
      name: 'flags',
      type: 'string' as const,
      label: 'Flags',
      defaultValue: '',
      description: 'Regex flags to use when Input Format is pattern (e.g., i, m, s, u, y)',
    },
    {
      name: 'maxNodes',
      type: 'number' as const,
      label: 'Max Nodes',
      defaultValue: 600,
      description: 'Safety limit for diagram size',
      validation: { min: 50, max: 5000, step: 50 },
    },
  ];

  render(context: VisualizerRenderContext): React.ReactElement {
    const { input, config, onEdit, editMode } = context;

    const inputFormat =
      config.inputFormat === 'pattern' || config.inputFormat === 'literal' || config.inputFormat === 'auto'
        ? (config.inputFormat as 'auto' | 'pattern' | 'literal')
        : 'auto';

    const flags = String(config.flags ?? '').trim();
    const maxNodes = typeof config.maxNodes === 'number' ? (config.maxNodes as number) : 600;

    // Accept empty input — show empty diagram with toolbar
    if (!input.trim()) {
      const emptyGraph = { nodes: [], edges: [], groups: [] };
      const diagramElement = <LazyRegexDiagramView graph={emptyGraph} editMode={editMode} onEdit={onEdit} rawInput={input} segments={[]} />;
      return (
        <React.Suspense fallback={<div className="flex items-center justify-center h-full text-sm text-muted-foreground">Loading diagram…</div>}>
          {diagramElement}
        </React.Suspense>
      );
    }

    const parsed = parseRegex(input, { inputFormat, flags });
    if (!parsed.ok) {
      return (
        <div className="p-4 text-red-500">
          <div className="font-semibold">Parse Error</div>
          <div className="text-sm mt-1">{parsed.error}</div>
        </div>
      );
    }

    let graph;
    try {
      graph = regexAstToGraph(parsed.ast, { maxNodes });
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Unable to build diagram';
      return (
        <div className="p-4 text-red-500">
          <div className="font-semibold">Diagram Error</div>
          <div className="text-sm mt-1">{message}</div>
        </div>
      );
    }

    // Build editable segments from graph nodes with source ranges
    const segments: RegexSegment[] = graph.nodes
      .filter((n) => n.range && n.kind !== 'start' && n.kind !== 'end' && n.kind !== 'junction')
      .sort((a, b) => a.range!.start - b.range!.start)
      .map((n) => ({
        id: n.id,
        label: n.label,
        kind: n.kind,
        sourceText: parsed.pattern.slice(n.range!.start, n.range!.end),
      }));

    return (
      <React.Suspense fallback={<div className="flex items-center justify-center h-full text-sm text-muted-foreground">Loading diagram…</div>}>
        <LazyRegexDiagramView
          graph={graph}
          editMode={editMode}
          onEdit={onEdit}
          rawInput={input}
          segments={segments}
          parsedPattern={parsed.pattern}
          parsedFlags={parsed.flags}
        />
      </React.Suspense>
    );
  }

  toText(input: string): string {
    return input;
  }
}

export const RegexRailroadVisualizerTool = new RegexRailroadVisualizerToolImpl();
